import React, { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Divider, Modal } from "semantic-ui-react";
import DropdownField from "../../../components/Editing/DropdownField";
import Editor from "../../../components/Editing/Editor";
import NumberField from "../../../components/Editing/NumberField";
import { editActions } from "../../../actions/editActions";
import TextField from "../../../components/Editing/TextField";
import {
  getParameterCategoryOptions,
  getDomainOptionsByDomainGroup,
  getFeatureOptions,
  DropdownOption
} from "../../../utils/pdApiUtils";
import TextIndicatorField from "../../../components/Editing/TextIndicatorField";
import { hasWriteAccess } from "../../../utils/utils";
import * as _ from "lodash";
import { EditLocalizationStatus } from "../../Editing/EditLocalizationStatus";
import ButtonField from "../../../components/Editing/ButtonField";

interface APMParametersProps {
  groupName: string;
  toggleModal: () => void;
  columns: State.ColumnMap;
}

interface ValidationCheck {
  isError: boolean;
  errorMessage: string;
}

const APMParameters: React.FC<APMParametersProps> = props => {
  const pdApi = useSelector((state: State.Root) => state.pdApi);
  const editView = useSelector((state: State.Root) => state.editView);
  const activeUser = useSelector((state: State.Root) => state.main.activeUser);
  const isLoading = useSelector((state: State.Root) => state.loadingStatus);
  const dispatch = useDispatch();
  const groupName = props.groupName as Api.GroupName;
  const editedObject = editView.editedObject;
  const storedObject = editView.storedObject;
  const isNew = !storedObject;
  const domainId = editedObject.domain.id;
  const domainOptions = pdApi ? getDomainOptionsByDomainGroup(pdApi, groupName) : [];
  const parameterCategoryOptions = pdApi ? getParameterCategoryOptions(domainId, pdApi) : [];
  const features = pdApi ? Object.entries(pdApi.features)
    .map(([, value]) => value)
    .filter(feature => feature.domain.id === domainId) : [];
  const featuresOptions = getFeatureOptions(features);
  if (groupName === "APM") {
    parameterCategoryOptions.push({
      key: -1,
      value: -1,
      text: "Select category"
    });
  }

  const parametersType = (groupName.toLowerCase() + "Parameters") as Api.OptionsGrud;

  const [isEditLocalizationStatusOpen, setIsEditLocalizationStatusOpen] = useState(false);
  const [locsOriginal, setLocsOriginal] = useState<Api.LocsParameter[]>([]);
  const [locsEdited, setLocsEdited] = useState<Api.LocsParameter[]>([]);
  const [userPermissions] = useState(hasWriteAccess(activeUser, groupName));
  const isShortenTextEnabled = useSelector((state: State.Root) => state.main.isShortenTextEnabled);

  const englishTranslationsChanged = (): boolean =>
    editedObject.name !== editedObject?.name ||
    editedObject.description !== editedObject?.description;

  const toggleEditLocalizationStatusModal = useCallback(() => {
    setIsEditLocalizationStatusOpen(prevState => !prevState);
  }, []);

  const setLocalizationsEdited = useCallback(
    (newLocalizations: Api.LocsParameter[], oldLocalizations: Api.LocsParameter[]) => {
      setLocsEdited(newLocalizations);
      setLocsOriginal(oldLocalizations);
      dispatch(
        editActions.setEdited({
          ...editedObject,
          name:
            newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.text ||
            editedObject?.name,
          description:
            newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.description ||
            editedObject?.description,
          locs: newLocalizations
        })
      );
    },
    [dispatch, editedObject]
  );

  const setLocalizationsNonEdited = useCallback((Localizations: Api.LocsParameter[]) => {
    setLocsOriginal(Localizations);
  }, []);

  const CustomNumberField = (
    id: string,
    label: string,
    value: number,
    readonly: boolean,
    disabled = false
  ): JSX.Element => {
    const checkValidation = isFieldValid(label.toLowerCase());
    return (
      <NumberField
        id={id}
        label={label}
        value={value}
        isError={checkValidation.isError}
        readonly={readonly}
        disabled={disabled}
        onChange={value => {
          dispatch(editActions.setEdited({ ...editedObject, [id.toLowerCase()]: value }));
        }}
        errorMessage={checkValidation.errorMessage}
      />
    );
  };

  const CustomDropDownField = (
    id: string,
    label: string,
    value: number | string | null,
    options: DropdownOption[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (value: any) => void,
    disabled = false,
    search = false
  ): JSX.Element => {
    const checkValidation = isFieldValid(label.toLowerCase());
    return (
      <DropdownField
        id={id}
        label={label}
        search={search}
        options={options}
        value={value}
        onChange={onChange}
        disabled={disabled}
        isError={checkValidation.isError}
        errorMessage={checkValidation.errorMessage}
      />
    );
  };

  const customTextField = (
    label: string,
    field: string,
    value: string | null,
    readOnly = false,
    disabled = false
  ): JSX.Element => {
    const checkValidation = isFieldValid(field);
    return (
      <TextField
        id={label}
        label={label}
        value={value}
        isError={checkValidation.isError}
        disabled={disabled}
        readonly={readOnly}
        errorMessage={checkValidation.errorMessage}
        onChange={changedValue =>
          dispatch(
            editActions.setEdited({
              ...editedObject,
              [field]: changedValue
            })
          )
        }
      />
    );
  };

  const checkCodeValid = (): ValidationCheck => {
    const targetType = (groupName.toLowerCase() + "Parameters") as keyof typeof pdApi;
    if (groupName === "APM" && pdApi?.apmParameters) {
      return pdApi.apmParameters.find(
        parameter =>
          parameter.code === editedObject.code &&
          parameter.id !== editedObject.id &&
          parameter.domain.id === editedObject.domain.id &&
          parameter.featureDbId === editedObject.featureDbId &&
          parameter.parameterCategory?.id === editedObject.parameterCategory?.id
      )
        ? { isError: true, errorMessage: "The code must be unique in Domain,Category and Features" }
        : { isError: false, errorMessage: "" };
    }
    if (editedObject.code <= 0) {
      return { isError: true, errorMessage: "The code must be greater than zero." };
    }
    if (!Number.isInteger(editedObject.code)) {
      return { isError: true, errorMessage: "The code must be an integer." };
    }
    if (
      pdApi && Array.isArray(pdApi[targetType]) &&
      (pdApi[targetType] as any[])?.find(p => p.code === editedObject.code && p.id !== editedObject.id)
    ) {
      return { isError: true, errorMessage: "The code must be unique." };
    } else return { isError: false, errorMessage: "" };
  };

  const isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);

  const checkEmptyString = (str: string): ValidationCheck => {
    if (isEmptyString(str)) {
      return { isError: true, errorMessage: "" };
    } else {
      return { isError: false, errorMessage: "" };
    }
  };

  const isFieldValid = (field: string): ValidationCheck => {
    switch (field) {
      case "code":
        return checkCodeValid();
      case "enumName":
        return checkEmptyString(editedObject.enumName);
      case "name":
        return checkEmptyString(editedObject.name);
      case "description":
        return checkEmptyString(editedObject.description);
      case "releaseStatus":
        return checkEmptyString(editedObject.releaseStatus);
      case "features":
        return featuresOptions.length === 0
          ? { isError: true, errorMessage: "Selected Domain Has No Features" }
          : { isError: false, errorMessage: "" };
      default:
        return { isError: false, errorMessage: "" };
    }
  };

  const isAllFieldsValid = (): boolean => {
    const temp = _.keys(editedObject);
    for (const key of temp) {
      if (key === "featureDbId" && featuresOptions.length === 0) {
        return false;
      }
      if (isFieldValid(key).isError) {
        return false;
      }
    }
    return true;
  };

  return (
    <Editor
      originalObject={storedObject}
      editedObject={editedObject}
      itemName="parameter"
      isValid={
        isAllFieldsValid() && !isLoading.isLoadingParameters && !isLoading.isLoadingApmParameters
      }
      hasWritePermission={hasWriteAccess(activeUser, groupName)}
      create={() => {
        dispatch(
          editActions.setEdited({
            ...editedObject,
            locs: locsEdited
          })
        );
        dispatch(editActions.add(parametersType));
      }}
      update={() => {
        dispatch(
          editActions.setEdited({
            ...editedObject,
            locs: locsEdited
          })
        );
        dispatch(editActions.update(parametersType));
      }}
      copy={() => dispatch(editActions.startEditing(editedObject, true))}
      cancel={() => dispatch(editActions.cancelEditing())}
      remove={{ callback: props.toggleModal }}
    >
      {CustomNumberField("Code", "Code", editedObject.code, !isNew)}
      {isNew
        ? CustomDropDownField(
            "domainName",
            "Domain name",
            editedObject.domain.id,
            domainOptions,
            (value: number) => {
              const domain = pdApi?.domains[value];
              dispatch(
                editActions.setEdited({
                  ...editedObject,
                  domain
                })
              );
            }
          )
        : customTextField(
            "Domain name",
            "domainName",
            (pdApi?.domains[editedObject.domain.id]?.enumName || "") +
              " [" +
              (pdApi?.domains[editedObject.domain.id]?.enumValue || "") +
              "]",
            false,
            true
          )}
      {CustomDropDownField(
        "category",
        "Category",
        editedObject.parameterCategory !== null ? editedObject.parameterCategory.id : -1,
        parameterCategoryOptions,
        (value: number) => {
          const parameterCategory =
            value === -1
              ? null
              : {
                  id: pdApi?.parameterCategories[value]?.id || "",
                  url: pdApi?.parameterCategories[value]?.url || ""
                };
          dispatch(
            editActions.setEdited({
              ...editedObject,
              parameterCategory
            })
          );
        },
        !userPermissions
      )}
      {customTextField("Sw Name", "enumName", editedObject.enumName, !userPermissions)}
      <Divider horizontal={true}>{"English translations"}</Divider>
      <TextIndicatorField
        id={"needsUpdateIndicator"}
        text={"Edited texts will be translated."}
        isWarning={true}
      />
      {customTextField("English name", "name", editedObject.name, !userPermissions)}
      {customTextField(
        "Description",
        "description",
        editedObject.description,
        !userPermissions
      )}{" "}
      <ButtonField
        id={"viewTranslationsButton"}
        label="View translations"
        onButtonClick={toggleEditLocalizationStatusModal}
      />
      <div>
        <Divider horizontal={true}>{`${groupName} properties`}</Divider>
      </div>
      {CustomDropDownField(
        "Features",
        "Features",
        editedObject.featureDbId,
        featuresOptions,
        (value: number) => {
          dispatch(
            editActions.setEdited({
              ...editedObject,
              featureDbId: value
            })
          );
        },
        !userPermissions
      )}
      {customTextField(
        "Release Status",
        "releaseStatus",
        editedObject.releaseStatus,
        !userPermissions
      )}{" "}
      <Modal size="large" open={isEditLocalizationStatusOpen && !!pdApi} className="data-sections-modal">
        {pdApi && (
          <EditLocalizationStatus
            itemType={"Parameter"}
            pdApi={pdApi}
            editedItem={{ ...editedObject, locs: locsEdited }}
            locsOriginal={locsOriginal || []}
            getOption="parameterLocalizations"
            columns={props.columns["Parameter Localization Status"]}
            toggleEditLocalizationStatusModal={toggleEditLocalizationStatusModal}
              setLocalizationsEdited={setLocalizationsEdited}
            setLocalizationsNonEdited={setLocalizationsNonEdited}
            isShortenTextEnabled={isShortenTextEnabled}
            englishTranslationChanged={englishTranslationsChanged()}
            userPermissions={userPermissions}
          />
        )}
      </Modal>
    </Editor>
  );
};

export default APMParameters;
